import React from "react";
import { Target, Eye } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/effects/Reveal";
import { GrowthChart } from "@/components/effects/GrowthChart";
import { useLanguage } from "@/i18n/LanguageContext";
import { ASSETS } from "@/data/assets";

export const About = () => {
  const { t } = useLanguage();
  return (
    <section id="about" className="relative overflow-hidden py-24 sm:py-32">
      <div className="absolute left-0 top-10 h-96 w-96 rounded-full bg-accent/10 blur-[150px]" />
      <div className="relative mx-auto max-w-7xl px-6">
        <div className="grid items-center gap-14 lg:grid-cols-2">
          {/* Left visual */}
          <Reveal>
            <div className="relative">
              <div className="glass relative overflow-hidden rounded-3xl p-10 shadow-card">
                <div className="absolute inset-0 bg-grid opacity-40" />
                <div className="absolute -right-8 -top-8 h-40 w-40 rounded-full bg-primary/20 blur-3xl animate-glow-pulse" />
                {/* Animated upward growth chart behind the mascot */}
                <div className="pointer-events-none absolute inset-x-6 top-6 bottom-28 z-0 flex items-center justify-center">
                  <GrowthChart className="h-full w-full animate-float-slow opacity-80" />
                </div>
                <img
                  src={ASSETS.mascotAbout}
                  alt="LUKI business fox mascot"
                  className="relative z-10 mx-auto w-4/5 animate-float-slow mask-radial drop-shadow-[0_24px_50px_rgba(255,107,0,0.3)]"
                />
                <div className="relative z-10 mt-6 grid grid-cols-2 gap-4">
                  {t.about.stats.map((s) => (
                    <div key={s.label} className="rounded-2xl border border-border/50 bg-secondary/30 p-4 text-center">
                      <p className="font-display text-2xl font-bold text-primary">{s.value}</p>
                      <p className="mt-1 text-xs text-muted-foreground">{s.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>

          {/* Right content */}
          <div>
            <SectionHeading tag={t.about.tag} title={t.about.title} subtitle={t.about.subtitle} align="left" />
            <div className="mt-6 space-y-4">
              {t.about.paragraphs.map((p, i) => (
                <Reveal key={i} delay={0.05 * i}>
                  <p className="text-sm leading-relaxed text-muted-foreground sm:text-base">{p}</p>
                </Reveal>
              ))}
            </div>

            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {[
                { icon: Target, ...t.about.mission },
                { icon: Eye, ...t.about.vision },
              ].map((m) => (
                <Reveal key={m.title}>
                  <div className="glass flex h-full flex-col rounded-2xl p-5">
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/15 text-primary">
                      <m.icon className="h-5 w-5" />
                    </span>
                    <h4 className="mt-4 font-display text-lg font-semibold text-foreground">{m.title}</h4>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{m.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
